import { toast } from 'react-hot-toast';

import { cartFetch } from '~/utils/cart-queue';
interface CartPayload {
  actionType: 'ADD' | 'UPDATE' | 'REMOVE' | 'GET';
  order_id: string;
  product_id?: string;
  quantity?: string;
  line_id?: string;
  filter?: Record<string, any>;
}

/**
 * Executes a cart operation with automatic token regeneration if an invalid order error is encountered
 * Returns the response data and a boolean indicating if the operation was retried with a new token
 */
export async function executeCartOperation(
  payload: CartPayload
): Promise<{ data: any; wasRetried: boolean }> {
  try {
    
    // First attempt with existing token
    const response = await cartFetch('/api/cart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    // Parse response data
    const responseData = await response.clone().json();
    
    // If the response is successful, return the data
    if (response.ok) {
      return { data: responseData, wasRetried: false };
    }
    
    // Handle error and check if it's related to an invalid order
    const errorMessage = responseData?.error || response.statusText;
    
    // Check if error message indicates an invalid order
    if (
      errorMessage.includes('Unable to locate order with id') || 
      errorMessage.includes('order not found') ||
      errorMessage.includes('order does not exist') ||
      errorMessage.includes('invalid input syntax for type uuid')
    ) {
      
      // Get store information
      const selectedStore = localStorage.getItem('selectedStore');
      if (!selectedStore) {
        throw new Error('No store selected, cannot regenerate token');
      }
      
      try {
        // Try to generate a new token directly
        const response = await fetch("/api/skunkworks", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ 
            store: selectedStore,
          }),
        });
        
        if (!response.ok) {
          throw new Error(`Failed to generate token: ${response.statusText}`);
        }
        
        const data = await response.json();
        const newToken = data.token;
        
        if (!newToken) {
          throw new Error('Failed to generate new token');
        }
        
        
        // Store the new token
        const tokenKey = `token_${selectedStore}`;
        localStorage.setItem(tokenKey, newToken);
        document.cookie = `${tokenKey}=${encodeURIComponent(newToken)}; path=/; max-age=${60 * 60 * 24 * 365}`;
        
        // Create new payload with updated token
        const retryPayload = {
          ...payload,
          order_id: newToken
        };
        
        
        // Retry the operation with the new token
        const retryResponse = await cartFetch('/api/cart', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(retryPayload),
        });
        
        const retryData = await retryResponse.json();
        
        // Check if retry was successful
        if (!retryResponse.ok) {
          throw new Error(`Retry failed: ${retryData?.error || retryResponse.statusText}`);
        }
        
        toast.success('Cart updated with new session');
        
        // Successful retry
        return { data: retryData, wasRetried: true };
      } catch (tokenError) {
        toast.error('Failed to refresh your session. Please try again.');
        throw tokenError;
      }
    }
    
    // Handle other types of errors
    let displayErrorMessage = 'Failed to update cart. Please try again.';
    if (errorMessage) {
      try {
        // Try to parse GraphQL error
        if (typeof errorMessage === 'string' && errorMessage.includes('GraphQL update error:')) {
          const parsedError = JSON.parse(errorMessage.replace('GraphQL update error: ', ''));
          if (Array.isArray(parsedError) && parsedError.length > 0 && parsedError[0]?.message) {
            displayErrorMessage = parsedError[0].message;
          }
        } else {
          displayErrorMessage = errorMessage;
        }
      } catch (parseError) {
        displayErrorMessage = typeof errorMessage === 'string' ? errorMessage : 'Unknown error';
      }
    }
    
    // Format user-friendly error messages
    if (displayErrorMessage.includes('requested quantity') && displayErrorMessage.includes('unavailable')) {
      displayErrorMessage = 'This product has no more stock';
    }
    
    // Show error to user
    toast.error(displayErrorMessage);
    throw new Error(displayErrorMessage);
  } catch (error: any) {
    throw error;
  }
}
